import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Global keyboard shortcuts. Mounted once at the App root. Translates
// physical keys into either:
//   - direct router actions (Alt+←/→ for back/forward)
//   - DOM custom events on `window` that pages/dialogs can subscribe to:
//       'shortcut:save'        — Ctrl+S
//       'shortcut:undo'        — Ctrl+Z
//       'shortcut:redo'        — Ctrl+Shift+Z / Ctrl+Y
//       'shortcut:new-item'    — N (only when no input is focused)
//       'shortcut:close'       — Escape (Radix dialogs handle their own,
//                                this is for non-Radix UIs)
//
// The dispatch model lets us avoid a global event-bus singleton; pages
// just useEffect → window.addEventListener('shortcut:save', handler).
//
// We deliberately ignore N when the user is typing in an input/textarea
// so it doesn't interfere with regular text entry.

const SHORTCUT_EVENTS = {
  save: "shortcut:save",
  undo: "shortcut:undo",
  redo: "shortcut:redo",
  newItem: "shortcut:new-item",
  close: "shortcut:close",
  search: "shortcut:search",
  /** "/" — focus the page's main search box (pages subscribe). */
  focusSearch: "shortcut:focus-search",
  /** "?" — open the shortcuts help sheet. */
  help: "shortcut:help",
  /** "g" then a letter — jump to a page. detail = the letter. */
  goTo: "shortcut:go-to",
} as const;

// "g" chord: pressing g then d/i/a/s/e within 800ms navigates. Kept
// in module scope so the listener below stays a plain function.
let pendingG = 0;

function dispatch(name: string, detail?: unknown) {
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}

export function useKeyboardShortcuts() {
  const navigate = useNavigate();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      // KeyboardEvent.key can be undefined for some IME/composition or
      // synthesized events (Chrome dead-key + autofill, mainly). Bail
      // before calling toLowerCase() on undefined.
      const key = typeof e.key === "string" ? e.key.toLowerCase() : "";
      if (!key) return;

      // Alt + Left/Right — back/forward navigation. Works even while
      // typing — Alt is rare in editing.
      if (e.altKey && !e.shiftKey && !mod) {
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          navigate(-1);
          return;
        }
        if (e.key === "ArrowRight") {
          e.preventDefault();
          navigate(1);
          return;
        }
      }

      // Ctrl/Cmd + S — save current dialog/form.
      if (mod && !e.altKey && !e.shiftKey && key === "s") {
        e.preventDefault();
        dispatch(SHORTCUT_EVENTS.save);
        return;
      }

      // Ctrl/Cmd + Z — undo.
      if (mod && !e.altKey && !e.shiftKey && key === "z") {
        e.preventDefault();
        dispatch(SHORTCUT_EVENTS.undo);
        return;
      }
      // Ctrl/Cmd + Shift + Z, or Ctrl/Cmd + Y — redo.
      if (
        (mod && e.shiftKey && key === "z") ||
        (mod && !e.shiftKey && key === "y")
      ) {
        e.preventDefault();
        dispatch(SHORTCUT_EVENTS.redo);
        return;
      }

      // Ctrl/Cmd + K — focus search (matches the Cmd-K pattern).
      if (mod && !e.altKey && !e.shiftKey && key === "k") {
        e.preventDefault();
        dispatch(SHORTCUT_EVENTS.search);
        return;
      }

      // Plain "/" — focus the main search on the page (like GitHub /
      // Linear). Suppressed while typing.
      if (!mod && !e.altKey && key === "/" && !isTypingTarget(e.target)) {
        e.preventDefault();
        dispatch(SHORTCUT_EVENTS.focusSearch);
        return;
      }

      // "?" — shortcut help.
      if (!mod && !e.altKey && key === "?" && !isTypingTarget(e.target)) {
        e.preventDefault();
        dispatch(SHORTCUT_EVENTS.help);
        return;
      }

      // "g" chords — g d (dashboard), g i (inventory), g a (auctions),
      // g s (sellers), g e (settings). Suppressed while typing.
      if (!mod && !e.altKey && !e.shiftKey && !isTypingTarget(e.target)) {
        const now = Date.now();
        if (key === "g") {
          pendingG = now;
          return;
        }
        if (pendingG && now - pendingG < 800 && "diase".includes(key)) {
          pendingG = 0;
          e.preventDefault();
          dispatch(SHORTCUT_EVENTS.goTo, key);
          return;
        }
        pendingG = 0;
      }

      // Plain N — new item. Suppressed while typing.
      if (
        !mod &&
        !e.altKey &&
        !e.shiftKey &&
        key === "n" &&
        !isTypingTarget(e.target)
      ) {
        dispatch(SHORTCUT_EVENTS.newItem);
        return;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navigate]);
}

// Tiny helper to subscribe to a shortcut event from any component.
export function onShortcut(
  name: keyof typeof SHORTCUT_EVENTS,
  handler: (detail?: unknown) => void,
): () => void {
  const eventName = SHORTCUT_EVENTS[name];
  const wrapped = (e: Event) => handler((e as CustomEvent).detail);
  window.addEventListener(eventName, wrapped);
  return () => window.removeEventListener(eventName, wrapped);
}

export { SHORTCUT_EVENTS };
